import React, { useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  CloudUploadOutlined as UploadIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];
const MAX_SIZE_KB = 500;
const MAX_SIZE_BYTES = MAX_SIZE_KB * 1024;

interface LogoUploadProps {
  value: string;
  onChange: (base64: string) => void;
  label?: string;
  disabled?: boolean;
}

const LogoUpload: React.FC<LogoUploadProps> = ({
  value,
  onChange,
  label = "Logo",
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (file: File) => {
    setError(null);

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Only PNG, JPG, SVG, and WebP files are allowed.");
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      setError(`File must be under ${MAX_SIZE_KB}KB.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Box
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        sx={{
          border: "2px dashed",
          borderColor: error ? "error.main" : "divider",
          borderRadius: 2,
          p: 2,
          textAlign: "center",
          bgcolor: "action.hover",
          cursor: disabled ? "default" : "pointer",
          transition: "border-color 0.2s",
          "&:hover": disabled
            ? {}
            : { borderColor: "primary.main" },
        }}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        {value ? (
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <Avatar
              src={value}
              variant="rounded"
              sx={{ width: 64, height: 64, bgcolor: "background.paper" }}
              imgProps={{ style: { objectFit: "contain" } }}
            />
            <Stack spacing={0.5} alignItems="flex-start">
              <Typography variant="caption" color="text.secondary">
                Click or drop to replace
              </Typography>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange("");
                  setError(null);
                }}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={0.5} alignItems="center">
            <UploadIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              Click or drag to upload logo
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PNG, JPG, SVG, WebP &middot; Max {MAX_SIZE_KB}KB
            </Typography>
          </Stack>
        )}
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        hidden
        onChange={handleChange}
        disabled={disabled}
      />
      {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
    </Stack>
  );
};

export default LogoUpload;
